/**
 * The drawing pad: paper that never runs out.
 *
 * The sheet is unbounded. One finger draws, two fingers slide and pinch it
 * about, the wheel zooms, and "show me everything" pulls the whole drawing
 * back into view — which is the affordance that stops an infinite canvas from
 * being a way to lose your picture. The page itself still never scrolls; it is
 * the camera that moves.
 *
 * Marks are kept in world coordinates, so a drawing started on a phone reopens
 * correctly on a laptop, and a stroke's width is divided by the zoom: a crayon
 * lays down the same line on the glass whatever the paper is doing underneath.
 */

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Hand, Home, Maximize, Star, Trash2, Undo2 } from 'lucide-preact';
import type { Stroke } from '../engine/stroke.ts';
import { BRUSHES, BRUSH_ORDER, createStroke, extendStroke, finishStroke } from '../engine/stroke.ts';
import { drawStroke } from '../engine/render.ts';
import type { Baked } from '../engine/tiles.ts';
import { bakedOf, TileLayer } from '../engine/tiles.ts';
import { token } from '../engine/palette.ts';
import { play } from '../engine/audio.ts';
import type { Camera, Scene } from '../lib/scene.ts';
import { cameraFitting, sceneBounds, toWorld } from '../lib/scene.ts';
import { panBy, pinch, zoomAt } from '../lib/gesture.ts';
import { createDraftKeeper, loadDraft } from '../lib/drafts.ts';
import { keepPicture } from '../lib/keep.ts';
import { useSurface } from '../hooks/useSurface.ts';
import { Chip, Coach, ConfirmSheet, CrayonRow, SIZES, SizePicker, ToyButton } from '../ui/controls.tsx';
import { fillPaper } from '../ui/paper.ts';

interface Props {
  onExit: () => void;
  onSaved: () => void;
  /** A picture being reopened from the shelf, or nothing for a fresh sheet. */
  initial?: Scene | null;
}

interface Gesture {
  a: number;
  b: number;
  prevA: { x: number; y: number };
  prevB: { x: number; y: number };
}

export function DoodlePad({ onExit, onSaved, initial }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tilesRef = useRef(new TileLayer());
  const bakedRef = useRef<Baked[]>([]);
  const activeRef = useRef(new Map<number, Stroke>());
  const camRef = useRef<Camera>({ x: 0, y: 0, scale: 1 });
  const pointsRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<Gesture | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fitPendingRef = useRef(false);

  const [color, setColor] = useState(() => token('--crayon-ink'));
  const [brush, setBrush] = useState<Stroke['brush']>('crayon');
  const [size, setSize] = useState<number>(SIZES[1]);
  const [strokeCount, setStrokeCount] = useState(0);
  const [panning, setPanning] = useState(false);
  const [askClear, setAskClear] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const settings = useRef({ color, brush, size, panning });
  settings.current = { color, brush, size, panning };

  const sceneNow = useCallback(
    (): Scene => ({ kind: 'doodle', strokes: bakedRef.current.map((b) => b.stroke) }),
    []
  );
  const draftRef = useRef(createDraftKeeper('doodle', sceneNow));

  // Whatever was on the paper last time comes back — either the picture being
  // reopened, or the unfinished drawing this browser was in the middle of.
  useEffect(() => {
    const keeper = draftRef.current;
    const restored = initial ?? loadDraft('doodle');
    if (restored && restored.strokes.length > 0) {
      bakedRef.current = restored.strokes.map(bakedOf);
      setStrokeCount(restored.strokes.length);
      fitPendingRef.current = true;
      setFlash(initial ? 'Keep drawing' : 'Your picture came back');
    }
    return () => keeper.stop();
  }, [initial]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1600);
    return () => clearTimeout(t);
  }, [flash]);

  /** Re-bake sharply once a pan or zoom has stopped moving. */
  const settleSoon = useCallback(() => {
    if (settleRef.current) clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => tilesRef.current.settle(camRef.current.scale), 220);
  }, []);

  const showEverything = useCallback((width: number, height: number) => {
    camRef.current = cameraFitting(sceneBounds(sceneNow()), width, height);
    tilesRef.current.settle(camRef.current.scale);
  }, [sceneNow]);

  const size2 = useSurface(canvasRef, {
    onResize: ({ width, height }) => {
      tilesRef.current.setDpr(Math.min(2, window.devicePixelRatio || 1));
      if (fitPendingRef.current && width > 0) {
        fitPendingRef.current = false;
        showEverything(width, height);
      }
    },

    onFrame: (ctx, _dt, { width, height }) => {
      const cam = camRef.current;
      fillPaper(ctx, cam, width, height);
      tilesRef.current.draw(ctx, cam, width, height, bakedRef.current);

      // Strokes still under a finger are not baked yet, so they ride on top.
      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.scale(cam.scale, cam.scale);
      ctx.translate(-cam.x, -cam.y);
      for (const s of activeRef.current.values()) drawStroke(ctx, s);
      ctx.restore();
    },

    onDown: (p, { width, height }) => {
      pointsRef.current.set(p.id, { x: p.pos.x, y: p.pos.y });

      // A second finger means the child wants to move the paper, not draw on
      // it — so the mark the first finger had started is taken back.
      if (pointsRef.current.size === 2) {
        activeRef.current.clear();
        const [a, b] = [...pointsRef.current.entries()];
        gestureRef.current = { a: a[0], b: b[0], prevA: { ...a[1] }, prevB: { ...b[1] } };
        return;
      }
      if (pointsRef.current.size > 2 || gestureRef.current) return;
      if (settings.current.panning) return;

      const { color: c, brush: b, size: s } = settings.current;
      const cam = camRef.current;
      const stroke = createStroke(b, c, s / cam.scale, Math.floor(Math.random() * 100000));
      extendStroke(stroke, toWorld(cam, p.pos.x, p.pos.y, width, height), cam.scale);
      activeRef.current.set(p.id, stroke);
    },

    onMove: (p, { width, height }) => {
      const previous = pointsRef.current.get(p.id);
      if (!previous) return;
      const next = { x: p.pos.x, y: p.pos.y };

      const g = gestureRef.current;
      if (g) {
        pointsRef.current.set(p.id, next);
        const a = pointsRef.current.get(g.a);
        const b = pointsRef.current.get(g.b);
        if (a && b) {
          camRef.current = pinch(camRef.current, g.prevA, g.prevB, a, b, width, height);
          g.prevA = { ...a };
          g.prevB = { ...b };
        }
        return;
      }

      if (settings.current.panning) {
        camRef.current = panBy(camRef.current, next.x - previous.x, next.y - previous.y);
        pointsRef.current.set(p.id, next);
        return;
      }

      pointsRef.current.set(p.id, next);
      const stroke = activeRef.current.get(p.id);
      const cam = camRef.current;
      if (stroke) extendStroke(stroke, toWorld(cam, next.x, next.y, width, height), cam.scale);
    },

    onUp: (p) => {
      pointsRef.current.delete(p.id);
      if (gestureRef.current) {
        if (pointsRef.current.size < 2) {
          gestureRef.current = null;
          tilesRef.current.settle(camRef.current.scale);
        }
        return;
      }
      if (settings.current.panning) {
        settleSoon();
        return;
      }

      const stroke = activeRef.current.get(p.id);
      if (!stroke) return;
      activeRef.current.delete(p.id);
      const baked = bakedOf(finishStroke(stroke));
      bakedRef.current.push(baked);
      tilesRef.current.settle(camRef.current.scale);
      tilesRef.current.invalidate(baked.box);
      setStrokeCount(bakedRef.current.length);
      draftRef.current.touch();
    },

    onWheel: (w, { width, height }) => {
      const factor = Math.exp(-w.deltaY * (w.pinch ? 0.01 : 0.0016));
      camRef.current = zoomAt(camRef.current, factor, w.pos.x, w.pos.y, width, height);
      settleSoon();
    },
  });

  const undo = () => {
    const last = bakedRef.current.pop();
    if (!last) return;
    tilesRef.current.invalidate(last.box);
    setStrokeCount(bakedRef.current.length);
    draftRef.current.touch();
    play('pop', 0.4);
  };

  const clear = () => {
    bakedRef.current = [];
    activeRef.current.clear();
    tilesRef.current.clear();
    camRef.current = { x: 0, y: 0, scale: 1 };
    setStrokeCount(0);
    setAskClear(false);
    draftRef.current.clear();
    play('whoosh', 0.6);
  };

  const save = () => {
    if (bakedRef.current.length === 0) return;
    draftRef.current.flush();
    play('yay', 0.7);
    setFlash('Kept it');
    void keepPicture(sceneNow()).then((result) => {
      if (result.ok) onSaved();
      else setFlash(result.message);
    });
  };

  const empty = strokeCount === 0 && activeRef.current.size === 0;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Drawing pad" />
        <div class="topbar">
          <div class="topbar__group">
            <ToyButton round label="Home" hideLabel onClick={onExit}>
              <Home size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton
              round
              label="Show me everything"
              hideLabel
              disabled={empty}
              onClick={() => showEverything(size2.width, size2.height)}
            >
              <Maximize size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
          <div class="topbar__group">
            <ToyButton round label="Undo" hideLabel disabled={empty} onClick={undo}>
              <Undo2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Start again" hideLabel disabled={empty} onClick={() => setAskClear(true)}>
              <Trash2 size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Keep this picture" hideLabel disabled={empty} onClick={save}>
              <Star size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>
        {flash && <Coach>{flash}</Coach>}
        {!flash && empty && size2.width > 0 && <Coach>Draw something. Two fingers slide the paper</Coach>}
      </div>

      <div class="dock">
        <CrayonRow value={color} onChange={setColor} />
        <div class="dock__row">
          <div class="dock__row dock__row--scroll">
            {BRUSH_ORDER.map((id) => (
              <Chip
                key={id}
                label={BRUSHES[id].label}
                pressed={brush === id && !panning}
                onClick={() => {
                  setBrush(id);
                  setPanning(false);
                }}
              />
            ))}
            <Chip label="Move" pressed={panning} onClick={() => setPanning((v) => !v)}>
              <Hand size={20} strokeWidth={2.75} />
            </Chip>
          </div>
          <SizePicker value={size} onChange={setSize} />
        </div>
      </div>

      {askClear && (
        <ConfirmSheet
          title="Start a new picture?"
          confirmLabel="Start again"
          onConfirm={clear}
          onCancel={() => setAskClear(false)}
        />
      )}
    </div>
  );
}
